import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import {
  ArrowRight, Check, Eye, EyeOff, Ship, Calendar, CreditCard, Link2,
  Smartphone, Zap, Users, Bell, ChevronRight, Tag, ChevronDown, ChevronUp, Loader
} from "lucide-react";
import { usePageT } from "./i18n/LanguageContext";
import { LanguageSwitcher } from "./components/LanguageSwitcher";

const API = `${process.env.REACT_APP_API_URL}/api`;
const LOGO_URL = "https://nautiqo.cloud/logo-nautiqo.png";

const heading = { fontFamily: "'Cabinet Grotesk', 'Outfit', sans-serif" };
const body = { fontFamily: "'Manrope', sans-serif" };

// ==================== TRANSLATIONS ====================
const translations = {
  it: {
    // Errors
    unknownError: "Errore sconosciuto",
    registrationError: "Errore nella registrazione",
    passwordTooShort: "La password deve essere di almeno 6 caratteri",
    passwordsDoNotMatch: "Le password non coincidono",
    // Register hero
    heroTitle: "Prova Nautiqo gratis per 30 giorni",
    heroSubtitle: "Il gestionale per noleggio barche, gommoni e charter nautici. Nessuna carta richiesta.",
    heroTagline: "Prenotazioni, flotta e pagamenti in un'unica piattaforma.",
    // Form labels
    companyNameLabel: "Nome attivita *",
    companyNamePlaceholder: "Es: Seawave Charter",
    firstNameLabel: "Il tuo nome",
    firstNamePlaceholder: "Es: Marco",
    emailLabel: "Email *",
    emailPlaceholder: "email@esempio.com",
    phoneLabel: "Telefono",
    phonePlaceholder: "+39 333 1234567",
    passwordLabel: "Password *",
    passwordPlaceholder: "Minimo 6 caratteri",
    confirmPasswordLabel: "Conferma password *",
    confirmPasswordPlaceholder: "Ripeti la password",
    // Coupon
    couponToggle: "Hai un codice promozionale?",
    couponPlaceholder: "es: ESTATE30",
    couponInvalid: "Codice non valido o scaduto",
    // Submit
    creatingAccount: "Creazione account...",
    startFree: "Inizia gratis — 30 giorni",
    alreadyHaveAccount: "Hai gia un account?",
    login: "Accedi",
    // Badges
    badge30Days: "30 giorni gratis",
    badgeNoCard: "Nessuna carta",
    badgeSetup: "Setup in 5 min",
    // Right panel
    setup5Min: "Setup in 5 minuti",
    rightPanelTitle: "Il tuo charter, sotto controllo",
    rightPanelDesc: "Prenotazioni, disponibilita, pagamenti. Tutto in un'unica piattaforma. Senza intermediari.",
    // Onboarding steps
    stepWelcome: "Benvenuto",
    stepHowItWorks: "Come funziona",
    stepStart: "Inizia",
    // Features
    featureFleetTitle: "Gestisci la tua flotta",
    featureFleetDesc: "Aggiungi le tue barche con prezzi stagionali, fasce orarie, foto e regole di prenotazione. Ogni barca ha il suo calendario indipendente.",
    featureCalendarTitle: "Calendario e prenotazioni",
    featureCalendarDesc: "Tutte le prenotazioni in un posto. Accetta, rifiuta, conferma. Invia email automatiche ai clienti ad ogni cambio di stato.",
    featurePaymentsTitle: "Pagamenti diretti",
    featurePaymentsDesc: "Incassa depositi e saldi direttamente sul tuo conto Stripe. Zero intermediari. Commissione solo su transazioni riuscite.",
    featureLinkTitle: "Link di prenotazione",
    featureLinkDesc: "Condividi il tuo link personalizzato su Instagram, WhatsApp o il tuo sito. I clienti prenotano e pagano in autonomia, 24/7.",
    // Quick start
    quickStart1Title: "Aggiungi la tua prima barca",
    quickStart1Desc: "Nome, foto, prezzi, fasce orarie",
    quickStart1Cta: "Aggiungi barca",
    quickStart2Title: "Collega Stripe per i pagamenti",
    quickStart2Desc: "Ricevi pagamenti online dai clienti",
    quickStart2Cta: "Collega Stripe",
    quickStart3Title: "Condividi il link prenotazione",
    quickStart3Desc: "I clienti prenotano in autonomia",
    quickStart3Cta: "Vai alla dashboard",
    // Step 1
    welcomeTitle: "Benvenuto in Nautiqo",
    accountReadyPrefix: "Il tuo account",
    accountReadySuffix: "è pronto. Hai 30 giorni per esplorare tutto senza limiti.",
    trialActivated: "Trial gratuito attivato",
    daysRemaining: "giorni rimasti",
    expiresOn: "Scade il",
    active: "ATTIVO",
    premiumFeatures: "Funzioni premium",
    allIncluded: "Tutte incluse",
    cardRequired: "Carta richiesta",
    no: "No",
    trialDuration: "Durata trial",
    days30: "30 giorni",
    afterTrial: "Dopo il trial",
    pricePerMonth: "€19,90/mese",
    discoverHowItWorks: "Scopri come funziona",
    // Step 2
    everythingTitle: "Tutto quello che puoi fare con Nautiqo",
    everythingDesc: "Una piattaforma completa per gestire il tuo charter. Dal calendario ai pagamenti.",
    availableOnMobile: "Disponibile anche su iOS e Android",
    manageFromPhone: "Gestisci tutto dal tuo smartphone quando sei in barca",
    download: "Scarica",
    back: "Indietro",
    startConfiguration: "Inizia la configurazione",
    // Step 3
    startIn3Steps: "Inizia in 3 passi",
    completeStepsDesc: "Completa questi passaggi per essere operativo in meno di 10 minuti.",
    manageEverywhere: "Puoi gestire tutto dal sito oppure scaricare l'app Nautiqo per iOS/Android e gestire le prenotazioni ovunque.",
    goToDashboard: "Vai alla dashboard",
    trialReminder: "Riceverai un promemoria via email prima che il trial scada.",
    // locale for date formatting
    dateLocale: "it-IT",
  },
  en: {
    unknownError: "Unknown error",
    registrationError: "Registration error",
    passwordTooShort: "Password must be at least 6 characters",
    passwordsDoNotMatch: "Passwords do not match",
    heroTitle: "Try Nautiqo free for 30 days",
    heroSubtitle: "The management software for boat, dinghy and yacht charter rentals. No card required.",
    heroTagline: "Bookings, fleet and payments in a single platform.",
    companyNameLabel: "Business name *",
    companyNamePlaceholder: "E.g. Seawave Charter",
    firstNameLabel: "Your name",
    firstNamePlaceholder: "E.g. Marco",
    emailLabel: "Email *",
    emailPlaceholder: "email@example.com",
    phoneLabel: "Phone",
    phonePlaceholder: "+39 333 1234567",
    passwordLabel: "Password *",
    passwordPlaceholder: "At least 6 characters",
    confirmPasswordLabel: "Confirm password *",
    confirmPasswordPlaceholder: "Repeat the password",
    couponToggle: "Have a promo code?",
    couponPlaceholder: "e.g. SUMMER30",
    couponInvalid: "Invalid or expired code",
    creatingAccount: "Creating account...",
    startFree: "Start free — 30 days",
    alreadyHaveAccount: "Already have an account?",
    login: "Sign in",
    badge30Days: "30 days free",
    badgeNoCard: "No card",
    badgeSetup: "Setup in 5 min",
    setup5Min: "Setup in 5 minutes",
    rightPanelTitle: "Your charter, under control",
    rightPanelDesc: "Bookings, availability, payments. All in a single platform. No middlemen.",
    stepWelcome: "Welcome",
    stepHowItWorks: "How it works",
    stepStart: "Get started",
    featureFleetTitle: "Manage your fleet",
    featureFleetDesc: "Add your boats with seasonal pricing, time slots, photos and booking rules. Each boat has its own independent calendar.",
    featureCalendarTitle: "Calendar and bookings",
    featureCalendarDesc: "All bookings in one place. Accept, decline, confirm. Send automatic emails to customers at every status change.",
    featurePaymentsTitle: "Direct payments",
    featurePaymentsDesc: "Collect deposits and balances directly into your Stripe account. Zero middlemen. Commission only on successful transactions.",
    featureLinkTitle: "Booking link",
    featureLinkDesc: "Share your personalized link on Instagram, WhatsApp or your website. Customers book and pay on their own, 24/7.",
    quickStart1Title: "Add your first boat",
    quickStart1Desc: "Name, photos, prices, time slots",
    quickStart1Cta: "Add boat",
    quickStart2Title: "Connect Stripe for payments",
    quickStart2Desc: "Receive online payments from customers",
    quickStart2Cta: "Connect Stripe",
    quickStart3Title: "Share the booking link",
    quickStart3Desc: "Customers book on their own",
    quickStart3Cta: "Go to dashboard",
    welcomeTitle: "Welcome to Nautiqo",
    accountReadyPrefix: "Your account",
    accountReadySuffix: "is ready. You have 30 days to explore everything without limits.",
    trialActivated: "Free trial activated",
    daysRemaining: "days remaining",
    expiresOn: "Expires on",
    active: "ACTIVE",
    premiumFeatures: "Premium features",
    allIncluded: "All included",
    cardRequired: "Card required",
    no: "No",
    trialDuration: "Trial duration",
    days30: "30 days",
    afterTrial: "After the trial",
    pricePerMonth: "€19.90/month",
    discoverHowItWorks: "Discover how it works",
    everythingTitle: "Everything you can do with Nautiqo",
    everythingDesc: "A complete platform to manage your charter. From the calendar to payments.",
    availableOnMobile: "Also available on iOS and Android",
    manageFromPhone: "Manage everything from your smartphone when you're on the boat",
    download: "Download",
    back: "Back",
    startConfiguration: "Start setup",
    startIn3Steps: "Get started in 3 steps",
    completeStepsDesc: "Complete these steps to be up and running in less than 10 minutes.",
    manageEverywhere: "You can manage everything from the website or download the Nautiqo app for iOS/Android and manage bookings anywhere.",
    goToDashboard: "Go to dashboard",
    trialReminder: "You'll receive an email reminder before your trial expires.",
    dateLocale: "en-US",
  },
  es: {
    unknownError: "Error desconocido",
    registrationError: "Error en el registro",
    passwordTooShort: "La contraseña debe tener al menos 6 caracteres",
    passwordsDoNotMatch: "Las contraseñas no coinciden",
    heroTitle: "Prueba Nautiqo gratis durante 30 días",
    heroSubtitle: "El software de gestión para alquiler de barcos, neumáticas y chárter náutico. Sin tarjeta.",
    heroTagline: "Reservas, flota y pagos en una sola plataforma.",
    companyNameLabel: "Nombre de la empresa *",
    companyNamePlaceholder: "Ej: Seawave Charter",
    firstNameLabel: "Tu nombre",
    firstNamePlaceholder: "Ej: Marco",
    emailLabel: "Email *",
    emailPlaceholder: "email@ejemplo.com",
    phoneLabel: "Teléfono",
    phonePlaceholder: "+39 333 1234567",
    passwordLabel: "Password *",
    passwordPlaceholder: "Mínimo 6 caracteres",
    confirmPasswordLabel: "Confirmar contraseña *",
    confirmPasswordPlaceholder: "Repite la contraseña",
    couponToggle: "¿Tienes un código promocional?",
    couponPlaceholder: "ej: VERANO30",
    couponInvalid: "Código no válido o caducado",
    creatingAccount: "Creando cuenta...",
    startFree: "Empieza gratis — 30 días",
    alreadyHaveAccount: "¿Ya tienes una cuenta?",
    login: "Iniciar sesión",
    badge30Days: "30 días gratis",
    badgeNoCard: "Sin tarjeta",
    badgeSetup: "Configuración en 5 min",
    setup5Min: "Configuración en 5 minutos",
    rightPanelTitle: "Tu chárter, bajo control",
    rightPanelDesc: "Reservas, disponibilidad, pagos. Todo en una sola plataforma. Sin intermediarios.",
    stepWelcome: "Bienvenido",
    stepHowItWorks: "Cómo funciona",
    stepStart: "Empezar",
    featureFleetTitle: "Gestiona tu flota",
    featureFleetDesc: "Añade tus barcos con precios de temporada, franjas horarias, fotos y reglas de reserva. Cada barco tiene su propio calendario independiente.",
    featureCalendarTitle: "Calendario y reservas",
    featureCalendarDesc: "Todas las reservas en un solo lugar. Acepta, rechaza, confirma. Envía emails automáticos a los clientes en cada cambio de estado.",
    featurePaymentsTitle: "Pagos directos",
    featurePaymentsDesc: "Cobra depósitos y saldos directamente en tu cuenta de Stripe. Cero intermediarios. Comisión solo en transacciones exitosas.",
    featureLinkTitle: "Enlace de reserva",
    featureLinkDesc: "Comparte tu enlace personalizado en Instagram, WhatsApp o tu web. Los clientes reservan y pagan por su cuenta, 24/7.",
    quickStart1Title: "Añade tu primer barco",
    quickStart1Desc: "Nombre, fotos, precios, franjas horarias",
    quickStart1Cta: "Añadir barco",
    quickStart2Title: "Conecta Stripe para los pagos",
    quickStart2Desc: "Recibe pagos online de los clientes",
    quickStart2Cta: "Conectar Stripe",
    quickStart3Title: "Comparte el enlace de reserva",
    quickStart3Desc: "Los clientes reservan por su cuenta",
    quickStart3Cta: "Ir al panel",
    welcomeTitle: "Bienvenido a Nautiqo",
    accountReadyPrefix: "Tu cuenta",
    accountReadySuffix: "está lista. Tienes 30 días para explorarlo todo sin límites.",
    trialActivated: "Prueba gratuita activada",
    daysRemaining: "días restantes",
    expiresOn: "Caduca el",
    active: "ACTIVA",
    premiumFeatures: "Funciones premium",
    allIncluded: "Todas incluidas",
    cardRequired: "Tarjeta requerida",
    no: "No",
    trialDuration: "Duración de la prueba",
    days30: "30 días",
    afterTrial: "Después de la prueba",
    pricePerMonth: "19,90 €/mes",
    discoverHowItWorks: "Descubre cómo funciona",
    everythingTitle: "Todo lo que puedes hacer con Nautiqo",
    everythingDesc: "Una plataforma completa para gestionar tu chárter. Del calendario a los pagos.",
    availableOnMobile: "Disponible también en iOS y Android",
    manageFromPhone: "Gestiona todo desde tu smartphone cuando estás en el barco",
    download: "Descargar",
    back: "Atrás",
    startConfiguration: "Iniciar configuración",
    startIn3Steps: "Empieza en 3 pasos",
    completeStepsDesc: "Completa estos pasos para estar operativo en menos de 10 minutos.",
    manageEverywhere: "Puedes gestionar todo desde la web o descargar la app de Nautiqo para iOS/Android y gestionar las reservas en cualquier lugar.",
    goToDashboard: "Ir al panel",
    trialReminder: "Recibirás un recordatorio por email antes de que caduque tu prueba.",
    dateLocale: "es-ES",
  },
  fr: {
    unknownError: "Erreur inconnue",
    registrationError: "Erreur lors de l'inscription",
    passwordTooShort: "Le mot de passe doit comporter au moins 6 caractères",
    passwordsDoNotMatch: "Les mots de passe ne correspondent pas",
    heroTitle: "Essayez Nautiqo gratuitement pendant 30 jours",
    heroSubtitle: "Le logiciel de gestion pour la location de bateaux, semi-rigides et charters nautiques. Sans carte.",
    heroTagline: "Réservations, flotte et paiements sur une seule plateforme.",
    companyNameLabel: "Nom de l'entreprise *",
    companyNamePlaceholder: "Ex : Seawave Charter",
    firstNameLabel: "Votre nom",
    firstNamePlaceholder: "Ex : Marco",
    emailLabel: "Email *",
    emailPlaceholder: "email@exemple.com",
    phoneLabel: "Téléphone",
    phonePlaceholder: "+39 333 1234567",
    passwordLabel: "Password *",
    passwordPlaceholder: "Au moins 6 caractères",
    confirmPasswordLabel: "Confirmer le mot de passe *",
    confirmPasswordPlaceholder: "Répétez le mot de passe",
    couponToggle: "Vous avez un code promo ?",
    couponPlaceholder: "ex : ETE30",
    couponInvalid: "Code invalide ou expiré",
    creatingAccount: "Création du compte...",
    startFree: "Commencez gratuitement — 30 jours",
    alreadyHaveAccount: "Vous avez déjà un compte ?",
    login: "Se connecter",
    badge30Days: "30 jours gratuits",
    badgeNoCard: "Sans carte",
    badgeSetup: "Configuration en 5 min",
    setup5Min: "Configuration en 5 minutes",
    rightPanelTitle: "Votre charter, sous contrôle",
    rightPanelDesc: "Réservations, disponibilité, paiements. Tout sur une seule plateforme. Sans intermédiaires.",
    stepWelcome: "Bienvenue",
    stepHowItWorks: "Comment ça marche",
    stepStart: "Commencer",
    featureFleetTitle: "Gérez votre flotte",
    featureFleetDesc: "Ajoutez vos bateaux avec tarifs saisonniers, créneaux horaires, photos et règles de réservation. Chaque bateau a son propre calendrier indépendant.",
    featureCalendarTitle: "Calendrier et réservations",
    featureCalendarDesc: "Toutes les réservations au même endroit. Acceptez, refusez, confirmez. Envoyez des emails automatiques aux clients à chaque changement de statut.",
    featurePaymentsTitle: "Paiements directs",
    featurePaymentsDesc: "Encaissez acomptes et soldes directement sur votre compte Stripe. Zéro intermédiaire. Commission uniquement sur les transactions réussies.",
    featureLinkTitle: "Lien de réservation",
    featureLinkDesc: "Partagez votre lien personnalisé sur Instagram, WhatsApp ou votre site. Les clients réservent et paient en toute autonomie, 24/7.",
    quickStart1Title: "Ajoutez votre premier bateau",
    quickStart1Desc: "Nom, photos, tarifs, créneaux horaires",
    quickStart1Cta: "Ajouter un bateau",
    quickStart2Title: "Connectez Stripe pour les paiements",
    quickStart2Desc: "Recevez des paiements en ligne de vos clients",
    quickStart2Cta: "Connecter Stripe",
    quickStart3Title: "Partagez le lien de réservation",
    quickStart3Desc: "Les clients réservent en autonomie",
    quickStart3Cta: "Aller au tableau de bord",
    welcomeTitle: "Bienvenue sur Nautiqo",
    accountReadyPrefix: "Votre compte",
    accountReadySuffix: "est prêt. Vous avez 30 jours pour tout explorer sans limites.",
    trialActivated: "Essai gratuit activé",
    daysRemaining: "jours restants",
    expiresOn: "Expire le",
    active: "ACTIF",
    premiumFeatures: "Fonctions premium",
    allIncluded: "Toutes incluses",
    cardRequired: "Carte requise",
    no: "Non",
    trialDuration: "Durée de l'essai",
    days30: "30 jours",
    afterTrial: "Après l'essai",
    pricePerMonth: "19,90 €/mois",
    discoverHowItWorks: "Découvrez comment ça marche",
    everythingTitle: "Tout ce que vous pouvez faire avec Nautiqo",
    everythingDesc: "Une plateforme complète pour gérer votre charter. Du calendrier aux paiements.",
    availableOnMobile: "Disponible aussi sur iOS et Android",
    manageFromPhone: "Gérez tout depuis votre smartphone lorsque vous êtes en mer",
    download: "Télécharger",
    back: "Retour",
    startConfiguration: "Démarrer la configuration",
    startIn3Steps: "Commencez en 3 étapes",
    completeStepsDesc: "Complétez ces étapes pour être opérationnel en moins de 10 minutes.",
    manageEverywhere: "Vous pouvez tout gérer depuis le site ou télécharger l'application Nautiqo pour iOS/Android et gérer vos réservations partout.",
    goToDashboard: "Aller au tableau de bord",
    trialReminder: "Vous recevrez un rappel par email avant l'expiration de votre essai.",
    dateLocale: "fr-FR",
  },
  de: {
    unknownError: "Unbekannter Fehler",
    registrationError: "Fehler bei der Registrierung",
    passwordTooShort: "Das Passwort muss mindestens 6 Zeichen lang sein",
    passwordsDoNotMatch: "Die Passwörter stimmen nicht überein",
    heroTitle: "Teste Nautiqo 30 Tage kostenlos",
    heroSubtitle: "Die Verwaltungssoftware für die Vermietung von Booten, Schlauchbooten und Yachtchartern. Keine Karte erforderlich.",
    heroTagline: "Buchungen, Flotte und Zahlungen auf einer einzigen Plattform.",
    companyNameLabel: "Firmenname *",
    companyNamePlaceholder: "z. B.: Seawave Charter",
    firstNameLabel: "Dein Name",
    firstNamePlaceholder: "z. B.: Marco",
    emailLabel: "E-Mail *",
    emailPlaceholder: "email@beispiel.com",
    phoneLabel: "Telefon",
    phonePlaceholder: "+39 333 1234567",
    passwordLabel: "Passwort *",
    passwordPlaceholder: "Mindestens 6 Zeichen",
    confirmPasswordLabel: "Passwort bestätigen *",
    confirmPasswordPlaceholder: "Passwort wiederholen",
    couponToggle: "Hast du einen Aktionscode?",
    couponPlaceholder: "z. B.: SOMMER30",
    couponInvalid: "Ungültiger oder abgelaufener Code",
    creatingAccount: "Konto wird erstellt...",
    startFree: "Kostenlos starten — 30 Tage",
    alreadyHaveAccount: "Hast du bereits ein Konto?",
    login: "Anmelden",
    badge30Days: "30 Tage kostenlos",
    badgeNoCard: "Keine Karte",
    badgeSetup: "Einrichtung in 5 Min.",
    setup5Min: "Einrichtung in 5 Minuten",
    rightPanelTitle: "Dein Charter, unter Kontrolle",
    rightPanelDesc: "Buchungen, Verfügbarkeit, Zahlungen. Alles auf einer einzigen Plattform. Ohne Vermittler.",
    stepWelcome: "Willkommen",
    stepHowItWorks: "So funktioniert's",
    stepStart: "Loslegen",
    featureFleetTitle: "Verwalte deine Flotte",
    featureFleetDesc: "Füge deine Boote mit saisonalen Preisen, Zeitfenstern, Fotos und Buchungsregeln hinzu. Jedes Boot hat seinen eigenen unabhängigen Kalender.",
    featureCalendarTitle: "Kalender und Buchungen",
    featureCalendarDesc: "Alle Buchungen an einem Ort. Annehmen, ablehnen, bestätigen. Sende bei jeder Statusänderung automatische E-Mails an die Kunden.",
    featurePaymentsTitle: "Direkte Zahlungen",
    featurePaymentsDesc: "Kassiere Anzahlungen und Restbeträge direkt auf dein Stripe-Konto. Keine Vermittler. Provision nur bei erfolgreichen Transaktionen.",
    featureLinkTitle: "Buchungslink",
    featureLinkDesc: "Teile deinen personalisierten Link auf Instagram, WhatsApp oder deiner Website. Kunden buchen und zahlen selbstständig, rund um die Uhr.",
    quickStart1Title: "Füge dein erstes Boot hinzu",
    quickStart1Desc: "Name, Fotos, Preise, Zeitfenster",
    quickStart1Cta: "Boot hinzufügen",
    quickStart2Title: "Verbinde Stripe für Zahlungen",
    quickStart2Desc: "Erhalte Online-Zahlungen von Kunden",
    quickStart2Cta: "Stripe verbinden",
    quickStart3Title: "Teile den Buchungslink",
    quickStart3Desc: "Kunden buchen selbstständig",
    quickStart3Cta: "Zum Dashboard",
    welcomeTitle: "Willkommen bei Nautiqo",
    accountReadyPrefix: "Dein Konto",
    accountReadySuffix: "ist bereit. Du hast 30 Tage, um alles ohne Einschränkungen zu erkunden.",
    trialActivated: "Kostenlose Testphase aktiviert",
    daysRemaining: "Tage verbleibend",
    expiresOn: "Läuft ab am",
    active: "AKTIV",
    premiumFeatures: "Premium-Funktionen",
    allIncluded: "Alle inklusive",
    cardRequired: "Karte erforderlich",
    no: "Nein",
    trialDuration: "Dauer der Testphase",
    days30: "30 Tage",
    afterTrial: "Nach der Testphase",
    pricePerMonth: "19,90 €/Monat",
    discoverHowItWorks: "Entdecke, wie es funktioniert",
    everythingTitle: "Alles, was du mit Nautiqo machen kannst",
    everythingDesc: "Eine komplette Plattform zur Verwaltung deines Charters. Vom Kalender bis zu den Zahlungen.",
    availableOnMobile: "Auch für iOS und Android verfügbar",
    manageFromPhone: "Verwalte alles von deinem Smartphone aus, wenn du auf dem Boot bist",
    download: "Herunterladen",
    back: "Zurück",
    startConfiguration: "Einrichtung starten",
    startIn3Steps: "Starte in 3 Schritten",
    completeStepsDesc: "Schließe diese Schritte ab, um in weniger als 10 Minuten einsatzbereit zu sein.",
    manageEverywhere: "Du kannst alles über die Website verwalten oder die Nautiqo-App für iOS/Android herunterladen und deine Buchungen überall verwalten.",
    goToDashboard: "Zum Dashboard",
    trialReminder: "Du erhältst eine E-Mail-Erinnerung, bevor deine Testphase abläuft.",
    dateLocale: "de-DE",
  },
};

const formatApiError = (detail, t) => {
  if (!detail) return t.unknownError;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || e?.message).filter(Boolean).join(". ");
  return String(detail);
};

// ==================== REGISTER PAGE ====================
export const RegisterPage = () => {
  const t = usePageT(translations);
  const [form, setForm] = useState({ company_name: '', email: '', password: '', confirmPassword: '', phone: '', first_name: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Coupon state
  const [showCoupon, setShowCoupon] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponStatus, setCouponStatus] = useState(null); // null | 'loading' | 'valid' | 'invalid'
  const [couponInfo, setCouponInfo] = useState(null);
  const couponTimer = useRef(null);

  const validateCoupon = async (code) => {
    const c = code.trim().toUpperCase();
    if (!c) { setCouponStatus(null); setCouponInfo(null); return; }
    setCouponStatus('loading');
    try {
      const { data } = await axios.get(`${API}/auth/validate-coupon?code=${encodeURIComponent(c)}`);
      setCouponStatus('valid');
      setCouponInfo(data);
    } catch (e) {
      setCouponStatus('invalid');
      setCouponInfo(null);
    }
  };

  const handleCouponChange = (val) => {
    const upper = val.toUpperCase();
    setCouponCode(upper);
    setCouponStatus(null);
    setCouponInfo(null);
    if (couponTimer.current) clearTimeout(couponTimer.current);
    if (upper.trim().length >= 3) {
      couponTimer.current = setTimeout(() => validateCoupon(upper), 700);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password.length < 6) {
      setError(t.passwordTooShort);
      return;
    }
    if (form.password !== form.confirmPassword) {
      setError(t.passwordsDoNotMatch);
      return;
    }
    setLoading(true);
    try {
      const { confirmPassword: _, ...submitData } = form;
      const payload = { ...submitData, source: 'web' };
      if (couponCode.trim()) payload.coupon_code = couponCode.trim().toUpperCase();
      const { data } = await axios.post(`${API}/auth/register`, payload);
      const token = data.access_token || data.token;
      if (token) {
        localStorage.setItem('nautiqo_token', token);
      }
      navigate('/onboarding');
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail, t) || t.registrationError);
    } finally {
      setLoading(false);
    }
  };

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex" style={body}>
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl"></div>
      </div>
      {/* Language switcher */}
      <div className="absolute top-4 right-4 z-20">
        <LanguageSwitcher variant="light" />
      </div>
      {/* Left panel - form */}
      <div className="flex-1 flex items-center justify-center px-4 py-8 md:py-12 relative z-10">
        <div className="w-full max-w-md">
          <Link to="/landing" className="flex items-center gap-2.5 mb-5 md:mb-10">
            <div className="w-10 h-10 rounded-[22%] overflow-hidden flex-shrink-0"><img src={LOGO_URL} alt="Nautiqo" className="w-full h-full scale-[1.06]" /></div>
            <span className="text-white font-bold text-xl tracking-tight" style={heading}>Nautiqo</span>
          </Link>

          <h1 className="text-2xl md:text-3xl font-bold text-white mb-2 tracking-tight" style={heading}>
            {t.heroTitle}
          </h1>
          <p className="text-slate-400 mb-2 leading-snug">
            {t.heroSubtitle}
          </p>
          <p className="text-slate-500 text-sm mb-5 md:mb-8 lg:hidden">
            {t.heroTagline}
          </p>
          <div className="hidden lg:block mb-8" />

          {error && (
            <div className="bg-red-500/10 border border-red-500/20 text-red-400 text-sm rounded-xl px-4 py-3 mb-6" data-testid="register-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-400 block mb-1.5">{t.companyNameLabel}</label>
              <input
                type="text"
                value={form.company_name}
                onChange={e => update('company_name', e.target.value)}
                placeholder={t.companyNamePlaceholder}
                required
                className="w-full px-4 py-3 bg-slate-900 border border-slate-800 rounded-xl text-white placeholder-slate-600 focus:border-blue-500 outline-none transition"
                data-testid="register-company"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-slate-400 block mb-1.5">{t.firstNameLabel}</label>
              <input
                type="text"
                value={form.first_name}
                onChange={e => update('first_name', e.target.value)}
                placeholder={t.firstNamePlaceholder}
                className="w-full px-4 py-3 bg-slate-900 border border-slate-800 rounded-xl text-white placeholder-slate-600 focus:border-blue-500 outline-none transition"
                data-testid="register-name"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-slate-400 block mb-1.5">{t.emailLabel}</label>
              <input
                type="email"
                value={form.email}
                onChange={e => update('email', e.target.value)}
                placeholder={t.emailPlaceholder}
                required
                className="w-full px-4 py-3 bg-slate-900 border border-slate-800 rounded-xl text-white placeholder-slate-600 focus:border-blue-500 outline-none transition"
                data-testid="register-email"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-slate-400 block mb-1.5">{t.phoneLabel}</label>
              <input
                type="tel"
                value={form.phone}
                onChange={e => update('phone', e.target.value)}
                placeholder={t.phonePlaceholder}
                className="w-full px-4 py-3 bg-slate-900 border border-slate-800 rounded-xl text-white placeholder-slate-600 focus:border-blue-500 outline-none transition"
                data-testid="register-phone"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-slate-400 block mb-1.5">{t.passwordLabel}</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={form.password}
                  onChange={e => update('password', e.target.value)}
                  placeholder={t.passwordPlaceholder}
                  required
                  className="w-full px-4 py-3 bg-slate-900 border border-slate-800 rounded-xl text-white placeholder-slate-600 focus:border-blue-500 outline-none transition pr-12"
                  data-testid="register-password"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition">
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-slate-400 block mb-1.5">{t.confirmPasswordLabel}</label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={form.confirmPassword}
                  onChange={e => update('confirmPassword', e.target.value)}
                  placeholder={t.confirmPasswordPlaceholder}
                  required
                  className="w-full px-4 py-3 bg-slate-900 border border-slate-800 rounded-xl text-white placeholder-slate-600 focus:border-blue-500 outline-none transition pr-12"
                  data-testid="register-confirm-password"
                />
                <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition">
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Coupon code — collapsible */}
            <div>
              <button
                type="button"
                onClick={() => setShowCoupon(v => !v)}
                className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-300 transition"
                data-testid="toggle-coupon"
              >
                <Tag className="w-3.5 h-3.5" />
                {t.couponToggle}
                {showCoupon ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              </button>

              {showCoupon && (
                <div className="mt-2">
                  <div className="relative">
                    <input
                      type="text"
                      value={couponCode}
                      onChange={e => handleCouponChange(e.target.value)}
                      placeholder={t.couponPlaceholder}
                      maxLength={20}
                      className={`w-full px-4 py-2.5 bg-slate-900 border rounded-xl text-white placeholder-slate-600 outline-none transition font-mono uppercase tracking-wider pr-10 text-sm ${
                        couponStatus === 'valid' ? 'border-emerald-500' :
                        couponStatus === 'invalid' ? 'border-red-500/60' :
                        'border-slate-800 focus:border-blue-500'
                      }`}
                      data-testid="coupon-input"
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {couponStatus === 'loading' && <Loader className="w-4 h-4 text-slate-400 animate-spin" />}
                      {couponStatus === 'valid' && <Check className="w-4 h-4 text-emerald-400" />}
                      {couponStatus === 'invalid' && <span className="text-red-400 text-xs font-medium">✕</span>}
                    </div>
                  </div>
                  {couponStatus === 'valid' && couponInfo && (
                    <div className="mt-2 flex items-center gap-2 px-3 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
                      <Tag className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
                      <p className="text-xs text-emerald-300 font-medium">{couponInfo.effect_label}</p>
                    </div>
                  )}
                  {couponStatus === 'invalid' && (
                    <p className="text-xs text-red-400 mt-1.5 ml-1">{t.couponInvalid}</p>
                  )}
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition disabled:opacity-50 flex items-center justify-center gap-2 mt-2"
              data-testid="register-submit"
            >
              {loading ? t.creatingAccount : <><span>{t.startFree}</span><ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>

          <p className="text-center text-sm text-slate-500 mt-6">
            {t.alreadyHaveAccount}{' '}
            <Link to="/login" className="text-blue-400 hover:text-blue-300 font-medium transition">{t.login}</Link>
          </p>

          <div className="flex items-center gap-4 mt-8 text-xs text-slate-600">
            <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-500" /> {t.badge30Days}</span>
            <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-500" /> {t.badgeNoCard}</span>
            <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-500" /> {t.badgeSetup}</span>
          </div>
        </div>
      </div>

      {/* Right panel - visual (desktop only) */}
      <div className="hidden lg:flex flex-1 items-center justify-center bg-gradient-to-br from-blue-600/20 to-slate-950 p-12 relative overflow-hidden">
        <div className="absolute top-1/4 right-1/4 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="max-w-sm w-full text-center">
          {/* Phone mockup */}
          <div className="relative inline-block mb-8">
            <div className="relative bg-slate-900 rounded-[2.8rem] p-2.5 shadow-2xl shadow-slate-900/60 ring-1 ring-white/10">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-6 bg-slate-900 rounded-b-2xl z-10" />
              <div className="relative bg-white rounded-[2.3rem] overflow-hidden w-52">
                <img
                  src="https://nautiqo.cloud/screen1.png"
                  alt="Nautiqo Dashboard"
                  className="w-full h-auto"
                />
              </div>
            </div>
            {/* Floating badges */}
            <div className="absolute -right-6 top-10 bg-emerald-500 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg shadow-emerald-500/30">
              {t.badge30Days}
            </div>
            <div className="absolute -left-8 bottom-16 bg-slate-800 border border-slate-700 text-white text-xs px-3 py-1.5 rounded-xl shadow-lg flex items-center gap-1.5">
              <span className="w-2 h-2 bg-emerald-400 rounded-full inline-block"></span>
              {t.setup5Min}
            </div>
          </div>
          <h2 className="text-2xl font-bold text-white mb-3" style={heading}>
            {t.rightPanelTitle}
          </h2>
          <p className="text-slate-400 leading-relaxed text-sm">
            {t.rightPanelDesc}
          </p>
        </div>
      </div>
    </div>
  );
};

// ==================== ONBOARDING WIZARD ====================
const getOnboardingSteps = (t) => [
  { id: 1, label: t.stepWelcome },
  { id: 2, label: t.stepHowItWorks },
  { id: 3, label: t.stepStart },
];

const getFeatures = (t) => [
  {
    icon: Ship,
    color: 'bg-blue-500/15 text-blue-400',
    title: t.featureFleetTitle,
    desc: t.featureFleetDesc,
  },
  {
    icon: Calendar,
    color: 'bg-indigo-500/15 text-indigo-400',
    title: t.featureCalendarTitle,
    desc: t.featureCalendarDesc,
  },
  {
    icon: CreditCard,
    color: 'bg-emerald-500/15 text-emerald-400',
    title: t.featurePaymentsTitle,
    desc: t.featurePaymentsDesc,
  },
  {
    icon: Link2,
    color: 'bg-amber-500/15 text-amber-400',
    title: t.featureLinkTitle,
    desc: t.featureLinkDesc,
  },
];

const getQuickStart = (t) => [
  { step: 1, title: t.quickStart1Title, desc: t.quickStart1Desc, href: '/admin/barche', cta: t.quickStart1Cta },
  { step: 2, title: t.quickStart2Title, desc: t.quickStart2Desc, href: '/admin/impostazioni', cta: t.quickStart2Cta },
  { step: 3, title: t.quickStart3Title, desc: t.quickStart3Desc, href: '/admin', cta: t.quickStart3Cta },
];

export const OnboardingPage = () => {
  const t = usePageT(translations);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1);
  const navigate = useNavigate();

  const token = localStorage.getItem('nautiqo_token');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const ONBOARDING_STEPS = getOnboardingSteps(t);
  const FEATURES = getFeatures(t);
  const QUICK_START = getQuickStart(t);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!token) { navigate('/login'); return; }
    axios.get(`${API}/auth/me`, { headers })
      .then(res => setUser(res.data))
      .catch(() => navigate('/login'))
      .finally(() => setLoading(false));
  }, []);

  // Trial end date
  const trialEnd = user?.subscription_end_date;
  const trialDays = trialEnd
    ? Math.max(0, Math.ceil((new Date(trialEnd) - new Date()) / (1000 * 60 * 60 * 24)))
    : 30;
  const trialEndFormatted = trialEnd
    ? new Date(trialEnd).toLocaleDateString(t.dateLocale, { day: 'numeric', month: 'long', year: 'numeric' })
    : '';

  if (loading) return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
      <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" style={body}>
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/3 right-1/4 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Top nav */}
        <div className="flex items-center justify-between px-6 py-5">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-[22%] overflow-hidden flex-shrink-0"><img src={LOGO_URL} alt="Nautiqo" className="w-full h-full scale-[1.06]" /></div>
            <span className="text-white font-bold text-lg tracking-tight" style={heading}>Nautiqo</span>
          </div>
          <div className="flex items-center gap-3">
            {/* Step indicator */}
            <div className="flex items-center gap-2">
              {ONBOARDING_STEPS.map((s, i) => (
                <div key={s.id} className="flex items-center gap-2">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                    step > s.id ? 'bg-emerald-500 text-white' :
                    step === s.id ? 'bg-blue-600 text-white' :
                    'bg-white/10 text-slate-500'
                  }`}>
                    {step > s.id ? <Check className="w-3.5 h-3.5" /> : s.id}
                  </div>
                  {i < ONBOARDING_STEPS.length - 1 && (
                    <div className={`w-8 h-0.5 rounded transition-all ${step > s.id ? 'bg-emerald-500/50' : 'bg-white/10'}`} />
                  )}
                </div>
              ))}
            </div>
            <LanguageSwitcher variant="light" />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 flex items-center justify-center px-4 py-8">

          {/* ── Step 1: Benvenuto ── */}
          {step === 1 && (
            <div className="w-full max-w-lg text-center">
              <div className="w-20 h-20 bg-emerald-500/15 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Check className="w-10 h-10 text-emerald-400" />
              </div>

              <h1 className="text-2xl sm:text-3xl font-bold text-white mb-3" style={heading}>
                {t.welcomeTitle}{user?.first_name ? `, ${user.first_name}` : ''}!
              </h1>
              <p className="text-slate-400 mb-8 max-w-md mx-auto">
                {t.accountReadyPrefix} <span className="text-white font-semibold">{user?.company_name}</span> {t.accountReadySuffix}
              </p>

              {/* Trial card */}
              <div className="bg-gradient-to-br from-blue-600/20 to-indigo-600/10 border border-blue-500/30 rounded-2xl p-6 mb-6 text-left">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-blue-500/20 rounded-xl flex items-center justify-center">
                    <Zap className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <p className="font-semibold text-white">{t.trialActivated}</p>
                    <p className="text-xs text-blue-300">{trialDays} {t.daysRemaining}{trialEndFormatted ? ` · ${t.expiresOn} ${trialEndFormatted}` : ''}</p>
                  </div>
                  <div className="ml-auto bg-emerald-500/20 border border-emerald-500/30 rounded-full px-3 py-1">
                    <span className="text-emerald-400 text-xs font-bold">{t.active}</span>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: t.premiumFeatures, value: t.allIncluded },
                    { label: t.cardRequired, value: t.no },
                    { label: t.trialDuration, value: t.days30 },
                    { label: t.afterTrial, value: t.pricePerMonth },
                  ].map(item => (
                    <div key={item.label} className="bg-white/5 rounded-xl p-3">
                      <p className="text-xs text-slate-500">{item.label}</p>
                      <p className="text-sm font-semibold text-white mt-0.5">{item.value}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Account confirmed */}
              <div className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 mb-6 flex items-center gap-3">
                <div className="w-8 h-8 bg-emerald-500/15 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Check className="w-4 h-4 text-emerald-400" />
                </div>
                <div className="text-left">
                  <p className="text-sm font-medium text-white">{user?.company_name}</p>
                  <p className="text-xs text-slate-500">{user?.email}</p>
                </div>
              </div>

              <button
                onClick={() => setStep(2)}
                className="w-full py-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition flex items-center justify-center gap-2 text-base"
                data-testid="onboarding-next-1"
              >
                {t.discoverHowItWorks} <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          )}

          {/* ── Step 2: Come funziona ── */}
          {step === 2 && (
            <div className="w-full max-w-2xl">
              <div className="text-center mb-8">
                <h1 className="text-2xl sm:text-3xl font-bold text-white mb-3" style={heading}>
                  {t.everythingTitle}
                </h1>
                <p className="text-slate-400">
                  {t.everythingDesc}
                </p>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                {FEATURES.map((f, i) => (
                  <div key={i} className="bg-white/5 border border-white/10 rounded-2xl p-5">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${f.color}`}>
                      <f.icon className="w-5 h-5" />
                    </div>
                    <h3 className="font-semibold text-white mb-1.5 text-sm">{f.title}</h3>
                    <p className="text-xs text-slate-400 leading-relaxed">{f.desc}</p>
                  </div>
                ))}
              </div>

              {/* App also available */}
              <div className="bg-slate-800/50 border border-white/10 rounded-xl px-4 py-3 mb-6 flex items-center gap-3">
                <Smartphone className="w-5 h-5 text-slate-400 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm text-white font-medium">{t.availableOnMobile}</p>
                  <p className="text-xs text-slate-500">{t.manageFromPhone}</p>
                </div>
                <a
                  href="https://apps.apple.com/app/nautiqo/id6740091498"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-blue-400 hover:text-blue-300 transition flex-shrink-0"
                >
                  {t.download}
                </a>
              </div>

              <div className="flex gap-3">
                <button onClick={() => setStep(1)} className="px-6 py-3 border border-white/10 text-slate-400 hover:text-white rounded-xl transition text-sm">
                  {t.back}
                </button>
                <button
                  onClick={() => setStep(3)}
                  className="flex-1 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition flex items-center justify-center gap-2"
                  data-testid="onboarding-next-2"
                >
                  {t.startConfiguration} <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* ── Step 3: Inizia ── */}
          {step === 3 && (
            <div className="w-full max-w-lg">
              <div className="text-center mb-8">
                <h1 className="text-2xl sm:text-3xl font-bold text-white mb-3" style={heading}>
                  {t.startIn3Steps}
                </h1>
                <p className="text-slate-400">
                  {t.completeStepsDesc}
                </p>
              </div>

              <div className="space-y-3 mb-8">
                {QUICK_START.map((item, i) => (
                  <div key={i} className="bg-white/5 border border-white/10 rounded-2xl p-4 flex items-center gap-4">
                    <div className="w-10 h-10 bg-blue-600/20 border border-blue-500/30 rounded-xl flex items-center justify-center flex-shrink-0">
                      <span className="text-blue-400 font-bold text-sm">{item.step}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-white">{item.title}</p>
                      <p className="text-xs text-slate-500">{item.desc}</p>
                    </div>
                    <Link
                      to={item.href}
                      className="text-xs text-blue-400 hover:text-blue-300 transition flex items-center gap-1 flex-shrink-0 font-medium"
                    >
                      {item.cta} <ArrowRight className="w-3 h-3" />
                    </Link>
                  </div>
                ))}
              </div>

              {/* Also: download app */}
              <div className="bg-slate-800/50 border border-white/10 rounded-xl px-4 py-3 mb-6 flex items-center gap-3">
                <Users className="w-5 h-5 text-slate-400 flex-shrink-0" />
                <div className="flex-1 text-xs text-slate-400">
                  {t.manageEverywhere}
                </div>
              </div>

              <div className="flex gap-3">
                <button onClick={() => setStep(2)} className="px-6 py-3.5 border border-white/10 text-slate-400 hover:text-white rounded-xl transition text-sm">
                  {t.back}
                </button>
                <button
                  onClick={() => { window.location.href = '/admin'; }}
                  className="flex-1 py-3.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition flex items-center justify-center gap-2"
                  data-testid="goto-dashboard"
                >
                  {t.goToDashboard} <ArrowRight className="w-5 h-5" />
                </button>
              </div>

              {/* Bell reminder */}
              <div className="mt-5 flex items-center justify-center gap-2 text-xs text-slate-500">
                <Bell className="w-3.5 h-3.5" />
                {t.trialReminder}
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};
